import ReactGA from 'react-ga4';
import { cookies, storageKeys } from 'static';

type EventParamValue = string | number | boolean | null | undefined;
type EventParams = Record<string, EventParamValue>;

type StoredAuth = {
  username?: string;
};

type FarmContext = {
  farmId?: string;
  farmName?: string;
  orgNumber?: string;
};

const EMAIL_HASH_USER_PROPERTY = 'sender_hash';
const FARM_ID_USER_PROPERTY = 'farm_id';
const ORG_NUMBER_USER_PROPERTY = 'org_nr';
const USER_PROPERTY_MAX_LENGTH = 36;
const SENDER_HASH_STORAGE_KEY = 'ga_sender_hash';
const DEFAULT_MEASUREMENT_ID = 'G-VY7LR37GNP';
const measurementId = (process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID).trim();

export const analyticsEnabled = process.env.REACT_APP_ENABLE_GA === 'true' && measurementId.length > 0;

let hasInitialized = false;
const emailHashCache = new Map<string, string>();

const normalizeString = (value: string | number | null | undefined): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length ? normalizedValue : undefined;
};

const normalizeEmail = (email?: string | null): string | undefined => {
  if (!email) {
    return undefined;
  }

  const normalizedEmail = email.trim().toLowerCase();
  return normalizedEmail.length ? normalizedEmail : undefined;
};

const normalizeOrgNumber = (orgNumber?: string | number | null): string | undefined => {
  const normalizedOrgNumber = normalizeString(orgNumber);

  if (!normalizedOrgNumber) {
    return undefined;
  }

  const digitsOnly = normalizedOrgNumber.replace(/\D/g, '');
  return digitsOnly || normalizedOrgNumber;
};

const getStoredAuthUsername = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const storedAuthRaw = localStorage.getItem(cookies.FarmersPlanToken);

  if (!storedAuthRaw) {
    return undefined;
  }

  try {
    const storedAuth = JSON.parse(storedAuthRaw) as StoredAuth;
    return normalizeEmail(storedAuth.username);
  } catch {
    return undefined;
  }
};

const toHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer), (value) => value.toString(16).padStart(2, '0')).join('');
};

const hashEmail = async (normalizedEmail: string): Promise<string | undefined> => {
  const cachedHash = emailHashCache.get(normalizedEmail);

  if (cachedHash) {
    return cachedHash;
  }

  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return undefined;
  }

  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalizedEmail));
  const emailHash = toHex(hashBuffer);

  emailHashCache.set(normalizedEmail, emailHash);

  return emailHash;
};

const getStoredFarmContext = (): FarmContext => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    farmId: normalizeString(sessionStorage.getItem(storageKeys.RecentFarmId)),
    farmName: normalizeString(sessionStorage.getItem(storageKeys.RecentFarmName)),
    orgNumber: normalizeOrgNumber(sessionStorage.getItem(storageKeys.RecentFarmOrgNumber)),
  };
};

const persistFarmContext = ({ farmId, farmName, orgNumber }: FarmContext): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (farmId) {
    sessionStorage.setItem(storageKeys.RecentFarmId, farmId);
  } else {
    sessionStorage.removeItem(storageKeys.RecentFarmId);
  }

  if (farmName) {
    sessionStorage.setItem(storageKeys.RecentFarmName, farmName);
  } else {
    sessionStorage.removeItem(storageKeys.RecentFarmName);
  }

  if (orgNumber) {
    sessionStorage.setItem(storageKeys.RecentFarmOrgNumber, orgNumber);
  } else {
    sessionStorage.removeItem(storageKeys.RecentFarmOrgNumber);
  }
};

const ensureInitialized = (): boolean => {
  if (!analyticsEnabled) {
    return false;
  }

  if (hasInitialized) {
    return true;
  }

  ReactGA.initialize(measurementId, {
    gtagOptions: {
      send_page_view: false,
    },
  });

  hasInitialized = true;
  return true;
};

const setUserProperties = (properties: Record<string, string | null>): void => {
  if (!ensureInitialized()) {
    return;
  }

  ReactGA.gtag('set', 'user_properties', properties);
};

const getFarmEventParams = (): Record<'farm_id' | 'farm_name' | 'org_nr', string> => {
  const farmContext = getStoredFarmContext();

  return {
    farm_id: farmContext.farmId || '',
    farm_name: farmContext.farmName || '',
    org_nr: farmContext.orgNumber || '',
  };
};

const getStoredSenderHash = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return normalizeString(sessionStorage.getItem(SENDER_HASH_STORAGE_KEY));
};

const persistSenderHash = (senderHash?: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!senderHash) {
    sessionStorage.removeItem(SENDER_HASH_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(SENDER_HASH_STORAGE_KEY, senderHash);
};

const resolveSenderHashFromStorage = async (): Promise<string | undefined> => {
  const cachedSenderHash = getStoredSenderHash();

  if (cachedSenderHash) {
    return cachedSenderHash;
  }

  const storedUsername = getStoredAuthUsername();

  if (!storedUsername) {
    return undefined;
  }

  const fullHash = await hashEmail(storedUsername);

  if (!fullHash) {
    return undefined;
  }

  const senderHash = fullHash.slice(0, USER_PROPERTY_MAX_LENGTH);
  persistSenderHash(senderHash);

  return senderHash;
};

const syncFarmUserProperty = async (farmId?: string, orgNumber?: string): Promise<void> => {
  if (!ensureInitialized()) {
    return;
  }

  const senderHash = await resolveSenderHashFromStorage();
  const properties: Record<string, string | null> = {
    [FARM_ID_USER_PROPERTY]: farmId ? farmId.slice(0, USER_PROPERTY_MAX_LENGTH) : null,
    [ORG_NUMBER_USER_PROPERTY]: orgNumber ? orgNumber.slice(0, USER_PROPERTY_MAX_LENGTH) : null,
    [EMAIL_HASH_USER_PROPERTY]: senderHash || null,
  };

  setUserProperties(properties);
};

export const initAnalytics = (): void => {
  ensureInitialized();
};

export const clearAnalyticsFarmContext = (): void => {
  persistFarmContext({});

  setUserProperties({
    [FARM_ID_USER_PROPERTY]: null,
    [ORG_NUMBER_USER_PROPERTY]: null,
  });
};

export const clearAnalyticsUser = (): void => {
  if (!ensureInitialized()) {
    return;
  }

  persistSenderHash(undefined);
  ReactGA.gtag('set', 'user_id', null);
  setUserProperties({
    [EMAIL_HASH_USER_PROPERTY]: null,
  });
};

export const setAnalyticsUserFromEmail = async (email?: string | null): Promise<void> => {
  if (!ensureInitialized()) {
    return;
  }

  const normalizedEmail = normalizeEmail(email);

  if (!normalizedEmail) {
    return;
  }

  const emailHash = await hashEmail(normalizedEmail);

  if (!emailHash) {
    return;
  }

  const senderHash = emailHash.slice(0, USER_PROPERTY_MAX_LENGTH);
  persistSenderHash(senderHash);

  ReactGA.gtag('set', 'user_id', senderHash);

  const farmContext = getStoredFarmContext();

  setUserProperties({
    [EMAIL_HASH_USER_PROPERTY]: senderHash,
    [FARM_ID_USER_PROPERTY]: farmContext.farmId ? farmContext.farmId.slice(0, USER_PROPERTY_MAX_LENGTH) : null,
    [ORG_NUMBER_USER_PROPERTY]: farmContext.orgNumber ? farmContext.orgNumber.slice(0, USER_PROPERTY_MAX_LENGTH) : null,
  });
};

export const setAnalyticsFarmContext = (
  farmId?: string | number | null,
  farmName?: string | null,
  orgNumber?: string | number | null,
): void => {
  const previousFarmContext = getStoredFarmContext();
  const normalizedFarmId = normalizeString(farmId);
  const normalizedFarmName = normalizeString(farmName);
  const normalizedOrgNumber = normalizeOrgNumber(orgNumber);

  persistFarmContext({
    farmId: normalizedFarmId,
    farmName: normalizedFarmName,
    orgNumber: normalizedOrgNumber,
  });

  void syncFarmUserProperty(normalizedFarmId, normalizedOrgNumber);

  const hasSwitchedFarm = Boolean(
    previousFarmContext.farmId
    && normalizedFarmId
    && previousFarmContext.farmId !== normalizedFarmId
  );

  if (hasSwitchedFarm) {
    trackAnalyticsEvent('farm_switched', {
      previous_farm_id: previousFarmContext.farmId || '',
      next_farm_id: normalizedFarmId,
    });
  }
};

export const trackAnalyticsEvent = (eventName: string, eventParams: EventParams = {}): void => {
  if (!ensureInitialized()) {
    return;
  }

  ReactGA.event(eventName, {
    ...eventParams,
    ...getFarmEventParams(),
  });
};

export const trackLoginSuccess = async (email?: string | null): Promise<void> => {
  await setAnalyticsUserFromEmail(email);
  trackAnalyticsEvent('login');
};

export const trackLogout = (): void => {
  trackAnalyticsEvent('logout');
};

export const trackScreenView = (screenName: string): void => {
  trackAnalyticsEvent('screen_view', {
    screen_name: screenName,
  });
};

export const trackPageExit = (pageName: string, timeOnPageSeconds: number): void => {
  trackAnalyticsEvent('page_exit', {
    page_name: pageName,
    time_on_page: Math.max(0, Math.round(timeOnPageSeconds)),
  });
};

export const trackAnimalListViewed = (animalCount: number): void => {
  trackAnalyticsEvent('animal_list_viewed', {
    animal_count: animalCount,
  });
};

export const trackAnimalViewed = (animalId: string | number): void => {
  trackAnalyticsEvent('animal_viewed', {
    animal_id: String(animalId),
  });
};

export const trackWeighingSessionStarted = (): void => {
  trackAnalyticsEvent('weighing_session_started');
};

export const trackAnimalWeighed = (animalId: string | number, weightKg: number): void => {
  trackAnalyticsEvent('animal_weighed', {
    animal_id: String(animalId),
    weight_kg: weightKg,
  });
};

export const trackWeighingSessionEnded = (animalsWeighed: number): void => {
  trackAnalyticsEvent('weighing_session_ended', {
    animals_weighed: animalsWeighed,
  });
};

export const trackSlaughterResultViewed = (animalId: string | number): void => {
  trackAnalyticsEvent('slaughter_result_viewed', {
    animal_id: String(animalId),
  });
};

export const trackBreedingActivityAdded = (animalId: string | number, activityType: string): void => {
  trackAnalyticsEvent('breeding_activity_added', {
    animal_id: String(animalId),
    activity_type: activityType,
  });
};

export const trackCalvingRegistered = (motherId: string | number, calfCount: number): void => {
  trackAnalyticsEvent('calving_registered', {
    mother_id: String(motherId),
    calf_count: calfCount,
  });
};

export const trackBoardViewed = (): void => {
  trackAnalyticsEvent('board_viewed');
};

export const trackTaskCreated = (taskType: string): void => {
  trackAnalyticsEvent('task_created', {
    task_type: taskType,
  });
};

export const trackTaskCompleted = (taskType: string): void => {
  trackAnalyticsEvent('task_completed', {
    task_type: taskType,
  });
};

export const trackReportViewed = (reportType: string): void => {
  trackAnalyticsEvent('report_viewed', {
    report_type: reportType,
  });
};

export const trackExportTriggered = (exportType: string, format: string): void => {
  trackAnalyticsEvent('export_triggered', {
    export_type: exportType,
    format,
  });
};
